import React from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Tooltip,
  Chip,
  Grid,
} from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  LocalShipping as ShippingIcon,
  Analytics as AnalyticsIcon,
  Psychology as AIIcon,
  Speed as SpeedIcon,
  Nature as EcoIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';

interface Level {
  id: number;
  name: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  requirements: string[];
  xpRequired: number;
  unlockedFeatures: string[];
}

const levels: Level[] = [
  {
    id: 1,
    name: 'The Starter',
    title: 'Level 1',
    description: 'Beginning your supply chain intelligence journey',
    icon: <ShippingIcon />,
    color: '#9CA3AF',
    requirements: ['Connect your first data source', 'View the dashboard'],
    xpRequired: 0,
    unlockedFeatures: ['Basic Dashboard', 'Simple Reports'],
  },
  {
    id: 2,
    name: 'The Operator',
    title: 'Level 2',
    description: 'Understanding your supply chain basics',
    icon: <AnalyticsIcon />,
    color: '#60A5FA',
    requirements: ['Add 3 suppliers', 'View resilience report', 'Run first query'],
    xpRequired: 100,
    unlockedFeatures: ['Resilience Analytics', 'Supplier Tracking'],
  },
  {
    id: 3,
    name: 'The Strategist',
    title: 'Level 3',
    description: 'Making data-driven decisions',
    icon: <AIIcon />,
    color: '#8B5CF6',
    requirements: ['Run a simulation', 'Achieve 70% resilience', '7-day login streak'],
    xpRequired: 300,
    unlockedFeatures: ['What-If Simulations', 'AI Recommendations', 'Advanced Charts'],
  },
  {
    id: 4,
    name: 'The Optimizer',
    title: 'Level 4',
    description: 'Proactively improving operations',
    icon: <SpeedIcon />,
    color: '#10B981',
    requirements: ['Reduce costs by 5%', 'Improve lead time', 'Resolve 5 disruptions'],
    xpRequired: 600,
    unlockedFeatures: ['Cost Optimizer', 'Predictive Alerts', 'Multi-Scenario Planning'],
  },
  {
    id: 5,
    name: 'The Sustainability Champion',
    title: 'Level 5',
    description: 'Building a greener supply chain',
    icon: <EcoIcon />,
    color: '#22C55E',
    requirements: ['Reduce carbon by 10%', 'Add sustainable supplier', 'Green certification'],
    xpRequired: 1000,
    unlockedFeatures: ['ESG Reporting', 'Carbon Calculator', 'Sustainability Roadmap'],
  },
  {
    id: 6,
    name: 'The Tycoon',
    title: 'Level 6',
    description: 'Mastering supply chain excellence',
    icon: <TrophyIcon />,
    color: '#F59E0B',
    requirements: ['Achieve 90% resilience', '30-day streak', 'Zero stockouts for a month'],
    xpRequired: 2000,
    unlockedFeatures: ['Executive Dashboard', 'API Access', 'Custom AI Agents'],
  },
];

interface ProgressSystemProps {
  currentXP: number;
  completedMilestones: string[];
  loginStreak: number;
}

/**
 * Progress System - RPG-style gamification for supply chain maturity
 * 
 * Treats business maturity like a video game level.
 * Hides complex features until users are ready for them.
 */
const ProgressSystem: React.FC<ProgressSystemProps> = ({
  currentXP = 250,
  completedMilestones = ['Connect your first data source', 'View the dashboard', 'Add 3 suppliers', 'View resilience report'],
  loginStreak = 5,
}) => {
  // Calculate current level
  const getCurrentLevel = (): number => {
    for (let i = levels.length - 1; i >= 0; i--) {
      if (currentXP >= levels[i].xpRequired) {
        return levels[i].id;
      }
    }
    return 1;
  };

  const currentLevel = getCurrentLevel();
  const currentLevelData = levels.find(l => l.id === currentLevel)!;
  const nextLevelData = levels.find(l => l.id === currentLevel + 1);
  
  // Calculate progress to next level
  const getProgressToNextLevel = (): number => {
    if (!nextLevelData) return 100;
    const currentLevelXP = currentLevelData.xpRequired;
    const xpForNextLevel = nextLevelData.xpRequired - currentLevelXP;
    const xpProgress = currentXP - currentLevelXP;
    return Math.min(100, (xpProgress / xpForNextLevel) * 100);
  };

  const progressToNext = getProgressToNextLevel();

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        borderRadius: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${currentLevelData.color}33 0%, ${currentLevelData.color}11 100%)`,
              border: `2px solid ${currentLevelData.color}`,
              color: currentLevelData.color,
            }}
          >
            {currentLevelData.icon}
          </Box>
          <Box>
            <Typography variant="h6" color="white" fontWeight="bold">
              {currentLevelData.name}
            </Typography>
            <Typography variant="caption" color="grey.400">
              {currentLevelData.description}
            </Typography>
          </Box>
        </Box>
        <Chip
          icon={<StarIcon sx={{ color: '#F59E0B' }} />}
          label={`${currentXP} XP`}
          sx={{
            background: 'rgba(245, 158, 11, 0.2)',
            border: '1px solid #F59E0B',
            color: '#F59E0B',
            fontWeight: 'bold',
          }}
        />
      </Box>

      {/* Progress to next level */}
      {nextLevelData && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="grey.400">
              Progress to {nextLevelData.name}
            </Typography>
            <Typography variant="caption" color="grey.400">
              {currentXP} / {nextLevelData.xpRequired} XP
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressToNext}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(255,255,255,0.1)',
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${currentLevelData.color} 0%, ${nextLevelData.color} 100%)`,
                borderRadius: 4,
              },
            }}
          />
        </Box>
      )}

      {/* Login streak */}
      <Box sx={{ mb: 3, p: 2, background: 'rgba(245, 158, 11, 0.1)', borderRadius: 2 }}>
        <Typography variant="body2" color="grey.300">
          🔥 <strong>{loginStreak}-Day Streak!</strong> Keep it up to unlock bonuses.
        </Typography>
      </Box>

      {/* Level progression */}
      <Typography variant="subtitle2" color="grey.400" gutterBottom>
        Your Journey
      </Typography>
      <Grid container spacing={1}>
        {levels.map((level) => {
          const isUnlocked = currentLevel >= level.id;
          const isCurrent = currentLevel === level.id;
          
          return (
            <Grid item xs={4} sm={2} key={level.id}>
              <Tooltip
                title={
                  <Box>
                    <Typography variant="subtitle2">{level.name}</Typography>
                    <Typography variant="caption">{level.description}</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="grey.400">Unlocks:</Typography>
                      {level.unlockedFeatures.map((f, i) => (
                        <Typography key={i} variant="caption" display="block">• {f}</Typography>
                      ))}
                    </Box>
                  </Box>
                }
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    textAlign: 'center',
                    background: isUnlocked
                      ? `linear-gradient(135deg, ${level.color}33 0%, ${level.color}11 100%)`
                      : 'rgba(255,255,255,0.05)',
                    border: isCurrent ? `2px solid ${level.color}` : '1px solid rgba(255,255,255,0.1)',
                    opacity: isUnlocked ? 1 : 0.5,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Box sx={{ color: isUnlocked ? level.color : 'grey.600', mb: 0.5 }}>
                    {isUnlocked ? level.icon : <LockIcon />}
                  </Box>
                  <Typography variant="caption" sx={{ color: isUnlocked ? 'white' : 'grey.600' }}>
                    Lv.{level.id}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

export default ProgressSystem;
